import { useCallback, useState } from 'react';
import { prepareErrorForDisplay, type ErrorDisplayInfo } from '../services/core/ApiErrorHandler';
import type { ExtendedApiError } from '../types/common/base.types';
import { appNotify } from '../utils/notifications';

interface UseErrorHandlerReturn {
  error: ErrorDisplayInfo | null;
  clearError: () => void;
  
  handleError: (error: unknown, options?: HandleErrorOptions) => ErrorDisplayInfo;
  notifyError: (error: unknown, options?: HandleErrorOptions) => ErrorDisplayInfo;
  
  withErrorHandling: <T>(
    operation: () => Promise<T>,
    options?: WithErrorHandlingOptions
  ) => Promise<T | null>;
}

interface HandleErrorOptions {
  showNotification?: boolean;
  customMessage?: string;
  
  logError?: boolean;
  notificationType?: 'error' | 'warning' | 'info';
}

interface WithErrorHandlingOptions extends HandleErrorOptions {
  successMessage?: string;
  silent?: boolean;
  
  onSuccess?: <T>(result: T) => void;
  
  onError?: (error: ErrorDisplayInfo) => void;
}

export function useErrorHandler(): UseErrorHandlerReturn {
  const [error, setError] = useState<ErrorDisplayInfo | null>(null);
  const clearError = useCallback(() => {
    setError(null);
  }, []);

  const handleError = useCallback((
    inputError: unknown, 
    options: HandleErrorOptions = {}
  ): ErrorDisplayInfo => {
    const {
      showNotification = true,
      customMessage,
      logError = true,
      notificationType = 'error'
    } = options;

    const displayInfo = prepareErrorForDisplay(inputError);
    
    const finalDisplayInfo = customMessage ? {
      ...displayInfo,
      message: customMessage
    } : displayInfo;

    if (logError && import.meta.env.DEV) {
      console.group(`🚨 Error handled by useErrorHandler`);
      console.error('Original error:', inputError);
      console.error('Display info:', finalDisplayInfo);
      console.groupEnd();
    }

    if (showNotification) {
      const shouldPersist = finalDisplayInfo.severity === 'error';
      const historyOptions = shouldPersist
        ? {
            persistToHistory: true,
            historyType: 'error' as const,
            historyTitle: 'Ошибка',
            metadata: {
              operationId: finalDisplayInfo.operationId,
              details: finalDisplayInfo.details,
            },
          }
        : undefined;

      if (finalDisplayInfo.severity === 'warning') {
        appNotify.warning('Предупреждение', finalDisplayInfo.message, historyOptions);
      } else if (finalDisplayInfo.severity === 'info') {
        appNotify.info('Информация', finalDisplayInfo.message, historyOptions);
      } else if (notificationType === 'warning') {
        appNotify.warning('Предупреждение', finalDisplayInfo.message, historyOptions);
      } else {
        appNotify.error('Ошибка', finalDisplayInfo.message, historyOptions);
      }
    }

    setError(finalDisplayInfo);

    return finalDisplayInfo;
  }, []);

  const notifyError = useCallback((
    inputError: unknown, 
    options: HandleErrorOptions = {}
  ): ErrorDisplayInfo => {
    return handleError(inputError, options);
  }, [handleError]);

  const withErrorHandling = useCallback(<T,>(
    operation: () => Promise<T>,
    options: WithErrorHandlingOptions = {}
  ): Promise<T | null> => {
    return (async () => {
      const {
        successMessage,
        silent = false,
        onSuccess,
        onError,
        ...errorOptions
      } = options;

      try {
        if (!silent) {
          setError(null);
        }

        const result = await operation();

        if (successMessage) {
          appNotify.success('Успешно', successMessage);
        }

        onSuccess?.(result);

        return result;
      } catch (error) {
        if (silent) {
          if (import.meta.env.DEV) {
            console.error('Silent error in withErrorHandling:', error);
          }
          return null;
        }

        const displayInfo = handleError(error, {
          logError: true,
          ...errorOptions
        });

        onError?.(displayInfo);

        return null;
      }
    })();
  }, [handleError]);

  return {
    error,
    clearError,
    handleError,
    notifyError, 
    withErrorHandling
  };
}

export function isExtendedApiError(error: unknown): error is ExtendedApiError {
  return error instanceof Error && error.name === 'ExtendedApiError';
}

export function getOperationId(error: unknown): string | undefined {
  if (isExtendedApiError(error)) {
    return error.operationId;
  }
  return undefined;
}

